import { useState, useRef, useEffect } from "react";
import { API_BASE } from '@/lib/api';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Baby, Send, Loader2, ArrowLeft, CheckCircle2, CreditCard, MessageCircle, Heart, Shield, Star, User, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API_URL = API_BASE;

const NannyCard = ({ nanny, selected, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    whileTap={{ scale: 0.98 }}
    onClick={() => onSelect(nanny.user_id)}
    className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${
      selected
        ? "border-pink-500 bg-pink-50 shadow-md"
        : "border-stone-200 bg-white hover:border-pink-300 hover:shadow-sm"
    }`}
    data-testid={`nanny-card-${nanny.user_id}`}
  >
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-300 to-rose-400 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {nanny.profile_picture ? (
          <img src={nanny.profile_picture} alt={nanny.name} className="w-full h-full object-cover rounded-full" />
        ) : (
          <User className="w-6 h-6 text-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-emerald-950 truncate">{nanny.name}</h4>
        <div className="flex items-center gap-2 text-sm text-stone-500">
          {nanny.avg_rating > 0 ? (
            <>
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              <span>{nanny.avg_rating.toFixed(1)}</span>
              <span className="text-stone-300">|</span>
              <span>{nanny.review_count} reviews</span>
            </>
          ) : (
            <span className="text-stone-400">New professional</span>
          )}
        </div>
      </div>
      {selected && (
        <CheckCircle2 className="w-6 h-6 text-pink-500 flex-shrink-0" />
      )}
    </div>
  </motion.div>
);

const NannyBookingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bookingReady, setBookingReady] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState("new");

  // Nanny selection state
  const [availableNannies, setAvailableNannies] = useState([]);
  const [selectedNannyId, setSelectedNannyId] = useState(null);
  const [loadingNannies, setLoadingNannies] = useState(false);
  const [nannyStep, setNannyStep] = useState(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  useEffect(() => {
    const initialMessage = {
      role: "assistant",
      content: "Hello! Welcome to Stack's Nanny Service. I'm here to help you find the perfect caregiver for your little ones.\n\nI'll guide you through booking a professional, background-checked nanny tailored to your family's needs. To get started, could you tell me how many children need care and their ages?"
    };
    setMessages([initialMessage]);
  }, []);

  // Fetch available nannies when booking data is ready
  useEffect(() => {
    if (bookingReady && bookingData && !nannyStep) {
      fetchAvailableNannies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingReady, bookingData]);

  const fetchAvailableNannies = async () => {
    setLoadingNannies(true);
    try {
      const res = await axios.get(`${API_URL}/api/professionals/available?service=nanny`);
      setAvailableNannies(res.data.professionals || []);
      setNannyStep(true);
    } catch (err) {
      console.error("Error fetching nannies:", err);
      toast.error("Could not load available nannies");
      setNannyStep(true);
    } finally {
      setLoadingNannies(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: "user", content: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/nanny/chat`, {
        session_id: sessionId,
        message: inputValue,
        user_email: user?.email || "guest@stack.com",
        user_name: user?.name || "Guest",
        user_phone: user?.phone || null,
        user_address: user?.address || null
      });

      setSessionId(response.data.session_id);
      
      let displayText = response.data.response;
      displayText = displayText.replace(/```json[\s\S]*?```/g, "").trim();
      
      const assistantMessage = { role: "assistant", content: displayText };
      setMessages(prev => [...prev, assistantMessage]);

      if (response.data.booking_ready && response.data.booking_data) {
        setBookingReady(true);
        setBookingData(response.data.booking_data);
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage.content);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleConfirmBooking = async () => {
    if (!bookingData || !sessionId) return;
    
    setIsBooking(true);
    try {
      const bookingPayload = {
        session_id: sessionId,
        number_of_children: parseInt(bookingData.number_of_children) || 1,
        children_ages: bookingData.children_ages || "Not specified",
        service_date: bookingData.service_date,
        service_time: bookingData.service_time,
        duration_hours: parseFloat(bookingData.duration_hours) || 4,
        is_recurring: bookingData.is_recurring || false,
        recurring_schedule: bookingData.recurring_schedule !== "none" ? bookingData.recurring_schedule : null,
        location: bookingData.location || user?.address || "Not specified",
        customer_name: user?.name || bookingData.customer_name || "Guest",
        customer_phone: user?.phone || bookingData.customer_phone || "Not provided",
        customer_email: user?.email || "guest@stack.com",
        allergies_medical: bookingData.allergies_medical !== "none" ? bookingData.allergies_medical : null,
        special_needs: bookingData.special_needs !== "none" ? bookingData.special_needs : null,
        preferred_activities: bookingData.preferred_activities,
        additional_responsibilities: bookingData.additional_responsibilities !== "none" ? bookingData.additional_responsibilities : null,
        selected_professional_id: selectedNannyId || null,
      };

      const response = await axios.post(`${API_URL}/api/nanny/booking`, bookingPayload);
      
      toast.success("Booking confirmed! Processing payment...");
      
      const hourlyRate = 25;
      const childrenMultiplier = 1 + ((bookingPayload.number_of_children - 1) * 0.20);
      const hasAdditional = bookingPayload.additional_responsibilities && bookingPayload.additional_responsibilities !== "none";
      const additionalRate = hasAdditional ? 5 : 0;
      const totalPrice = Math.round((hourlyRate + additionalRate) * bookingPayload.duration_hours * childrenMultiplier);
      
      const serviceData = {
        booking_id: response.data.id,
        booking_type: "nanny",
        amount: totalPrice,
        customer_email: bookingPayload.customer_email,
        customer_name: bookingPayload.customer_name,
        service_type: "nanny",
        pickup_address: bookingPayload.location || "",
        dropoff_address: "",
        targeted_professional_id: selectedNannyId || null,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API_URL}/api/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your nanny...");
          navigate("/");
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setIsBooking(false);
      } else {
        const paymentResponse = await axios.post(`${API_URL}/api/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Booking error:", error);
      console.error("Error response:", error.response?.data);
      let errorMsg = "Failed to create booking. Please try again.";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setIsBooking(false);
    }
  };

  const selectedNanny = availableNannies.find(n => n.user_id === selectedNannyId);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white" data-testid="nanny-booking-page">
      <Navbar />

      <section className="pt-28 pb-8 px-4 md:px-0">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-4 text-stone-600 hover:text-pink-700"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Baby className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-emerald-950">Nanny Service</h1>
              <p className="text-stone-600">Professional childcare tailored to your family</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { icon: MessageCircle, text: "Personalized Matching" },
              { icon: Shield, text: "Background Checked" },
              { icon: Heart, text: "Nurturing Care" }
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-stone-600">
                <feature.icon className="w-4 h-4 text-pink-500" />
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          <div className="bg-pink-100 border border-pink-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-pink-800">
              <strong>Note:</strong> Stack connects families with independent, professional nannies. All caregivers are background-checked and vetted for your peace of mind.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 px-4 md:px-0">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-stone-200 overflow-hidden" data-testid="chat-container">
            <div className="bg-gradient-to-r from-pink-600 to-rose-600 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <Baby className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Nanny Assistant</h3>
                <p className="text-xs text-pink-100">Your Childcare Concierge</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-xs text-pink-100">Online</span>
              </div>
            </div>

            <div className="h-[450px] overflow-y-auto p-6 space-y-4 bg-stone-50" data-testid="messages-area">
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-pink-600 text-white rounded-br-md"
                          : "bg-white text-stone-800 shadow-sm border border-stone-100 rounded-bl-md"
                      }`}
                      data-testid={`message-${message.role}`}
                    >
                      <p className="whitespace-pre-wrap text-sm md:text-base">{message.content}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-pink-500" />
                      <span className="text-sm text-stone-500">Nanny Assistant is typing...</span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Nanny Selection Step */}
            {bookingReady && bookingData && nannyStep && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="bg-gradient-to-r from-pink-50 to-rose-50 border-t border-pink-200 p-6"
                data-testid="nanny-selection-section"
              >
                <div className="mb-4">
                  <h4 className="font-semibold text-emerald-950 mb-1 flex items-center gap-2">
                    <User className="w-5 h-5 text-pink-500" />
                    Choose Your Nanny
                  </h4>
                  <p className="text-sm text-stone-500">
                    Select a nanny you prefer, or skip to let us match you automatically.
                  </p>
                </div>

                {loadingNannies ? (
                  <div className="flex items-center justify-center py-8 gap-2" data-testid="loading-nannies">
                    <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
                    <span className="text-sm text-stone-500">Finding available nannies...</span>
                  </div>
                ) : availableNannies.length > 0 ? (
                  <div className="space-y-3 max-h-[250px] overflow-y-auto mb-4">
                    {availableNannies.map((nanny) => (
                      <NannyCard
                        key={nanny.user_id}
                        nanny={nanny}
                        selected={selectedNannyId === nanny.user_id}
                        onSelect={setSelectedNannyId}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 mb-4" data-testid="no-nannies-available">
                    <p className="text-stone-500 text-sm mb-2">No nannies are online right now.</p>
                    <p className="text-stone-400 text-xs">Your request will be broadcast to all available nannies after payment.</p>
                  </div>
                )}

                {selectedNannyId && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-pink-100 rounded-lg" data-testid="selected-nanny-badge">
                    <CheckCircle2 className="w-4 h-4 text-pink-600" />
                    <span className="text-sm text-pink-800 font-medium">
                      Selected: {selectedNanny?.name}
                    </span>
                    <button
                      onClick={() => setSelectedNannyId(null)}
                      className="ml-auto text-xs text-pink-600 hover:text-pink-800 underline"
                      data-testid="deselect-nanny-btn"
                    >
                      Remove
                    </button>
                  </div>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAvailableNannies}
                  className="text-pink-600 hover:text-pink-800 mb-4"
                  data-testid="refresh-nannies-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Refresh list
                </Button>
              </motion.div>
            )}

            {/* Payment Confirmation */}
            {bookingReady && bookingData && nannyStep && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="bg-gradient-to-r from-pink-50 to-rose-50 border-t border-pink-200 p-6"
                data-testid="booking-confirmation"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-emerald-950 mb-2">Ready to Confirm Your Booking!</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-stone-600 mb-4">
                      <div><strong>Date:</strong> {bookingData.service_date}</div>
                      <div><strong>Time:</strong> {bookingData.service_time}</div>
                      <div><strong>Children:</strong> {bookingData.number_of_children}</div>
                      <div><strong>Duration:</strong> {bookingData.duration_hours} hours</div>
                      {selectedNanny && (
                        <div className="col-span-2">
                          <strong>Nanny:</strong> {selectedNanny.name}
                          {selectedNanny.avg_rating > 0 && ` (${selectedNanny.avg_rating.toFixed(1)} stars)`}
                        </div>
                      )}
                    </div>
                    <SavedCardsSelector
                      email={user?.email}
                      name={user?.name}
                      onCardSelect={setSelectedCardId}
                      selectedCardId={selectedCardId}
                      disabled={isBooking}
                    />
                    <Button
                      onClick={handleConfirmBooking}
                      disabled={isBooking}
                      className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                      data-testid="confirm-booking-btn"
                    >
                      {isBooking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {selectedNannyId ? "Confirm & Request Nanny" : "Confirm & Pay"}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            <form onSubmit={sendMessage} className="border-t border-stone-200 p-4 bg-white">
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  autoFocus
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Tell us about your childcare needs..."
                  className="flex-1 rounded-full border-stone-300 focus:border-pink-400 focus:ring-pink-400"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="rounded-full bg-pink-600 hover:bg-pink-700 text-white px-6"
                  data-testid="send-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-stone-400 mt-2 text-center">
                All nannies are background-checked and vetted for your family's safety
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NannyBookingPage;
